import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { useLayoutStore } from '@/stores/layoutStore'
import { Layout } from '@/types/devices'

export function useLayout(id: string) {
  const hydrate = useLayoutStore(s => s.hydrate)

  return useQuery({
    queryKey: ['layout', id],
    queryFn: async () => {
      const data = await apiClient.get<Layout>(`/api/v1/layouts/${id}`)
      hydrate(data)
      return data
    },
  })
}

export function useLayouts() {
  return useQuery({
    queryKey: ['layouts'],
    queryFn: () => apiClient.get<Layout[]>('/api/v1/layouts'),
  })
}

export function useCreateLayout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      apiClient.post<Layout>('/api/v1/layouts', data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['layouts'] }),
  })
}

export function useDeleteLayout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/v1/layouts/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['layouts'] }),
  })
}

export function useRenameLayout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      name,
      version,
    }: {
      id: string
      name: string
      version: number
    }) => apiClient.put(`/api/v1/layouts/${id}`, { name, version }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['layouts'] }),
  })
}

export function useSaveLayout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      layoutId,
      mutations,
      version,
    }: {
      layoutId: string
      mutations: any[]
      version: number
    }) =>
      apiClient.post(`/api/v1/layouts/${layoutId}/batch`, {
        mutations,
        version,
      }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['layout', variables.layoutId],
      })
    },
  })
}
